import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'reactstrap';
import { api } from '../../../../../../../config';
import { getAuthToken } from '../../../../../../../auth/tokenvalidation';
import {
  DetailItem,
  SectionCard,
} from '../../../../../../../shared/offer-domain/ui';
import {
  pickLatestCounter,
  isSameId,
  isSameHtml,
  CounterDetailItem,
  CounterValueBlock,
  resolvePaymentSelectLabel,
  resolvePaymentCurrencyLabel,
  mergeSelectOptions,
} from '../../../../../../../shared/offer-domain/payment';

export { pickLatestCounter };

const SectionTitle = ({ title, subtitle }) => (
  <div className='mb-3'>
    <h5 className='fw-bold text-dark mb-1'>{title}</h5>
    {subtitle && <p className='text-muted small mb-0'>{subtitle}</p>}
  </div>
);

const unwrapItems = result => {
  const data = result?.data ?? result;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.Items)) return data.Items;
  if (Array.isArray(data)) return data;
  return [];
};

const itemName = item => item?.name ?? item?.Name ?? '';

const mapSelectItems = (list, getLabel) =>
  (Array.isArray(list) ? list : [])
    .map(item => ({
      value: item?.id ?? item?.Id ?? '',
      label: getLabel(item),
    }))
    .filter(item => item.value && item.label);

const fetchJson = async (url, token) => {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
};

const pickId = (id, entity) => id || entity?.id || entity?.Id || '';

const PaymentComplianceView = ({
  procurement,
  procurementMitraId = null,
  paymentRespond = null,
  complianceRespond = null,
}) => {
  const token = getAuthToken();
  const [invoiceQuantityOptions, setInvoiceQuantityOptions] = useState([]);
  const [modeOptions, setModeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [lawOptions, setLawOptions] = useState([]);
  const [arbitrationOptions, setArbitrationOptions] = useState([]);

  const latestPaymentCounter = useMemo(
    () =>
      pickLatestCounter(procurement?.counterPaymentTerm, procurementMitraId),
    [procurement?.counterPaymentTerm, procurementMitraId]
  );
  const latestComplianceCounter = useMemo(
    () => pickLatestCounter(procurement?.counterCompliance, procurementMitraId),
    [procurement?.counterCompliance, procurementMitraId]
  );

  useEffect(() => {
    if (!token) return;

    const loadInvoiceQuantity = async () => {
      try {
        const result = await fetchJson(
          `${api.API_URL}/invoicing-quantity`,
          token
        );
        setInvoiceQuantityOptions(
          mapSelectItems(unwrapItems(result), itemName)
        );
      } catch {
        setInvoiceQuantityOptions([]);
      }
    };

    const loadCurrency = async () => {
      try {
        const result = await fetchJson(`${api.API_URL}/paymentcurrency`, token);
        setCurrencyOptions(
          mapSelectItems(unwrapItems(result), item =>
            item?.currencyName || item?.CurrencyName
              ? `${item.currencyName ?? item.CurrencyName} (${item.currencyCode ?? item.CurrencyCode})`
              : itemName(item)
          )
        );
      } catch {
        setCurrencyOptions([]);
      }
    };

    const loadLaw = async () => {
      try {
        const result = await fetchJson(`${api.API_URL}/law`, token);
        setLawOptions(mapSelectItems(unwrapItems(result), itemName));
      } catch {
        setLawOptions([]);
      }
    };

    const loadArbitration = async () => {
      try {
        const result = await fetchJson(
          `${api.API_URL}/law/getAll-arbitration`,
          token
        );
        setArbitrationOptions(mapSelectItems(unwrapItems(result), itemName));
      } catch {
        setArbitrationOptions([]);
      }
    };

    loadInvoiceQuantity();
    loadCurrency();
    loadLaw();
    loadArbitration();
  }, [token]);

  const modeInvoicingQuantityIds = useMemo(() => {
    const ids = new Set();
    const add = value => {
      if (value != null && value !== '') ids.add(String(value));
    };
    add(procurement?.invoicingQuantityId);
    add(
      pickId(
        latestPaymentCounter?.invoicingQuantityId,
        latestPaymentCounter?.invoicingQuantity
      )
    );
    return [...ids];
  }, [procurement?.invoicingQuantityId, latestPaymentCounter]);

  useEffect(() => {
    if (!token || !modeInvoicingQuantityIds.length) {
      setModeOptions([]);
      return;
    }

    let cancelled = false;

    const loadModes = async () => {
      try {
        const results = await Promise.all(
          modeInvoicingQuantityIds.map(invoicingQuantityId =>
            fetchJson(
              `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity?InvoicingQuantityId=${invoicingQuantityId}`,
              token
            )
          )
        );
        if (cancelled) return;
        setModeOptions(
          mergeSelectOptions(
            ...results.map(result =>
              mapSelectItems(unwrapItems(result), itemName)
            )
          )
        );
      } catch {
        if (!cancelled) setModeOptions([]);
      }
    };

    loadModes();
    return () => {
      cancelled = true;
    };
  }, [token, modeInvoicingQuantityIds]);

  const originalCurrencyLabel = resolvePaymentCurrencyLabel(
    procurement?.paymentCurrency,
    procurement?.paymentCurrencyId,
    currencyOptions
  );
  const counterCurrencyLabel = resolvePaymentCurrencyLabel(
    latestPaymentCounter?.paymentCurrency,
    latestPaymentCounter?.paymentCurrencyId,
    currencyOptions
  );

  return (
    <>
      <SectionCard>
        <SectionTitle
          title='Payment Terms'
          subtitle='Choose the payment methods and terms that apply.'
        />
        <Row className='gy-4 mb-3'>
          <Col lg={4}>
            <DetailItem
              label='Invoicing Quantity'
              value={resolvePaymentSelectLabel(
                procurement?.invoicingQuantity,
                invoiceQuantityOptions,
                procurement?.invoicingQuantityId
              )}
            />
          </Col>
          <Col lg={4}>
            <DetailItem
              label='Mode'
              value={resolvePaymentSelectLabel(
                procurement?.modeInvoicingQuantity,
                modeOptions,
                procurement?.modeInvoicingQuantityId
              )}
            />
          </Col>
          <Col lg={4}>
            <DetailItem label='Currency' value={originalCurrencyLabel} />
          </Col>
          <Col lg={12}>
            <DetailItem
              label='Description of Payment Term'
              value={procurement?.descriptionOfPaymentTerms}
              isHtml
            />
          </Col>
        </Row>
        {latestPaymentCounter && (
          <CounterValueBlock title='Counter Value'>
            <Row className='gy-4'>
              <Col lg={4}>
                <CounterDetailItem
                  label='Invoicing Quantity'
                  value={resolvePaymentSelectLabel(
                    latestPaymentCounter.invoicingQuantity,
                    invoiceQuantityOptions,
                    latestPaymentCounter.invoicingQuantityId
                  )}
                  unchanged={isSameId(
                    latestPaymentCounter.invoicingQuantityId,
                    procurement?.invoicingQuantityId,
                    latestPaymentCounter.invoicingQuantity,
                    procurement?.invoicingQuantity
                  )}
                />
              </Col>
              <Col lg={4}>
                <CounterDetailItem
                  label='Mode'
                  value={resolvePaymentSelectLabel(
                    latestPaymentCounter.modeInvoicingQuantity,
                    modeOptions,
                    latestPaymentCounter.modeInvoicingQuantityId
                  )}
                  unchanged={isSameId(
                    latestPaymentCounter.modeInvoicingQuantityId,
                    procurement?.modeInvoicingQuantityId,
                    latestPaymentCounter.modeInvoicingQuantity,
                    procurement?.modeInvoicingQuantity
                  )}
                />
              </Col>
              <Col lg={4}>
                <CounterDetailItem
                  label='Currency'
                  value={counterCurrencyLabel}
                  unchanged={
                    isSameId(
                      latestPaymentCounter.paymentCurrencyId,
                      procurement?.paymentCurrencyId,
                      latestPaymentCounter.paymentCurrency,
                      procurement?.paymentCurrency
                    ) ||
                    (counterCurrencyLabel !== '-' &&
                      counterCurrencyLabel === originalCurrencyLabel)
                  }
                />
              </Col>
              {!isSameHtml(
                latestPaymentCounter.descriptionOfPaymentTerms,
                procurement?.descriptionOfPaymentTerms
              ) && (
                <Col lg={12}>
                  <CounterDetailItem
                    label='Description of Payment Term'
                    value={latestPaymentCounter.descriptionOfPaymentTerms}
                    isHtml
                  />
                </Col>
              )}
            </Row>
          </CounterValueBlock>
        )}
        {paymentRespond}
      </SectionCard>

      <SectionCard>
        <SectionTitle
          title='Compliance'
          subtitle='Confirm the compliance requirements for this procurement.'
        />
        <Row className='gy-4 mb-3'>
          <Col lg={6}>
            <DetailItem
              label='Law'
              value={resolvePaymentSelectLabel(
                procurement?.law,
                lawOptions,
                procurement?.lawId
              )}
            />
          </Col>
          <Col lg={6}>
            <DetailItem
              label='Arbitration'
              value={resolvePaymentSelectLabel(
                procurement?.arbitration,
                arbitrationOptions,
                procurement?.arbitrationId
              )}
            />
          </Col>
          <Col lg={12}>
            <DetailItem
              label='Other Terms & Condition'
              value={procurement?.otherTermsAndCondition}
              isHtml
            />
          </Col>
          <Col lg={12}>
            <DetailItem
              label='Additional Notes'
              value={procurement?.additionalNotes}
              isHtml
            />
          </Col>
        </Row>
        {latestComplianceCounter && (
          <CounterValueBlock title='Counter Value'>
            <Row className='gy-4'>
              <Col lg={6}>
                <CounterDetailItem
                  label='Law'
                  value={resolvePaymentSelectLabel(
                    latestComplianceCounter.law,
                    lawOptions,
                    latestComplianceCounter.lawId
                  )}
                  unchanged={isSameId(
                    latestComplianceCounter.lawId,
                    procurement?.lawId,
                    latestComplianceCounter.law,
                    procurement?.law
                  )}
                />
              </Col>
              <Col lg={6}>
                <CounterDetailItem
                  label='Arbitration'
                  value={resolvePaymentSelectLabel(
                    latestComplianceCounter.arbitration,
                    arbitrationOptions,
                    latestComplianceCounter.arbitrationId
                  )}
                  unchanged={isSameId(
                    latestComplianceCounter.arbitrationId,
                    procurement?.arbitrationId,
                    latestComplianceCounter.arbitration,
                    procurement?.arbitration
                  )}
                />
              </Col>
              {!isSameHtml(
                latestComplianceCounter.otherTermsAndCondition,
                procurement?.otherTermsAndCondition
              ) && (
                <Col lg={12}>
                  <CounterDetailItem
                    label='Other Terms & Condition'
                    value={latestComplianceCounter.otherTermsAndCondition}
                    isHtml
                  />
                </Col>
              )}
              {!isSameHtml(
                latestComplianceCounter.additionalNotes,
                procurement?.additionalNotes
              ) && (
                <Col lg={12}>
                  <CounterDetailItem
                    label='Additional Notes'
                    value={latestComplianceCounter.additionalNotes}
                    isHtml
                  />
                </Col>
              )}
            </Row>
          </CounterValueBlock>
        )}
        {complianceRespond}
      </SectionCard>
    </>
  );
};

export default PaymentComplianceView;
